/**
 * Represents a Sheet class that manages various aspects of a sheet, including its layout, question parts, and content objects.
 */
import RepositoryEntry from './RepositoryEntry';
class Sheet extends RepositoryEntry {
    /**
     * Constructs a new Sheet instance.
     * @param {Object} entry - The entry object.
     * @param {Class} Layout - The Layout class.
     * @param {Class} QuestionPart - The QuestionPart class.
     * @param {Class} ContentObject - The ContentObject class.
     * @param {Service} ContentObjectRegistryService - The ContentObjectRegistryService service.
     * @param {Object} $injector - The AngularJS injector service.
     */
    constructor(entry, Layout, QuestionPart, ContentObject, ContentObjectRegistryService, $injector) {
        super(entry, $injector);
        this.entry = entry;
        this.Layout = Layout;
        this.QuestionPart = QuestionPart;
        this.ContentObject = ContentObject;
        this.ContentObjectRegistryService = ContentObjectRegistryService;
        this.$injector = $injector;
        this.init();
    }

    /**
     * Initializes the sheet by setting up readables, accessables, references, and validators.
     * @returns {Sheet} The current Sheet instance.
     */
    init() {
        this.readables({
            'type': String
        });

        this.accessables({
            'title': String,
            'shortTitle': String,
            'bookmarked': Boolean,
            'useQuestionParts': Boolean,
            'useCustomTitle': Boolean,
            'preRelease': Boolean,
            'journal': Boolean
        });

        this.references({
            'layout': this.Layout,
            'questionParts': this.QuestionPart,
            'previewText': this.ContentObject,
            'previewImages': '*'
        });

        this.validators({
            'QIGEmptyQPValidator': 'QIGEmptyQPValidator',
            'ContainerEmptyValidator': 'ContainerEmptyValidator'
        });

        return this;
    }

    /**
     * Checks if the sheet is a background sheet.
     * @returns {boolean} True if the sheet is a background sheet, false otherwise.
     */
    isBackground() {
        return this.data.questions === undefined;
    }

    /**
     * Checks if the sheet is a regular sheet.
     * @returns {boolean} True if the sheet is a regular sheet, false otherwise.
     */
    isSheet() {
        return !this.isPreRelease() && !this.isJournal();
    }

    /**
     * Calculates the total marks of all question parts in the sheet.
     * @returns {number} The total marks.
     */
    getTotalMarks() {
        let questionParts = this.getQuestionParts(),
            totalMarks = 0,
            idx;

        for (idx = 0; idx < questionParts.length; idx++) {
            totalMarks += parseInt(questionParts[idx].getMarks());
        }

        return totalMarks;
    }

    /**
     * Gets the total status string of the sheet based on the status of its question parts.
     * @returns {string} The status string ('completed', 'not-started', or 'partly-completed').
     */
    getTotalStatusString() {
        let counter = 0;
        angular.forEach(this.getQuestionParts(), function (value, key) {
            if (value.getStatus() !== 0) {
                counter++;
            }
        });
        if (counter === this.getQuestionParts().length) {
            return 'completed';
        } else if (counter === 0) {
            return 'not-started';
        } else {
            return 'partly-completed';
        }
    }

    /**
     * Calculates the total progress of the sheet based on the status of its question parts.
     * @returns {number} The total progress.
     */
    getTotalProgress() {
        let questionParts = this.getQuestionParts(),
            stateSum = 0,
            sheetStatus = 0,
            idx;

        for (idx = 0; idx < questionParts.length; idx++) {
            stateSum += questionParts[idx].getStatus();
        }
        stateSum /= questionParts.length;
        return stateSum;
    }

    /**
     * Gets the total progress string of the sheet based on the status of its question parts.
     * @returns {string} The progress string ('finished', 'started', or 'not-started').
     */
    getTotalProgressString() {
        let state = this.getTotalProgress(),
            result = 'not-started';

        if (state === 1) {
            result = 'finished';
        } else if (state > 0) {
            result = 'started';
        }

        return result;
    }

    /**
     * Checks if the sheet has any bookmarks.
     * @returns {boolean} True if the sheet has bookmarks, false otherwise.
     */
    hasBookmarks() {
        let hasBookmarks = false,
            questionParts = this.getQuestionParts(),
            idx;

        for (idx = 0; idx < questionParts.length && !hasBookmarks; idx++) {
            hasBookmarks = hasBookmarks || questionParts[idx].isBookmarked();
        }

        return hasBookmarks;
    }

    /**
     * Gets the bookmarks from the sheet.
     * @returns {Array} The bookmarks.
     */
    getBookmarks() {
        if (isCMS && !this.$config().isOnEditSheet()) return [];

        let bookmarks = [],
            questionParts = this.getQuestionParts(),
            idx;

        for (idx = 0; idx < questionParts.length; idx++) {
            if (questionParts[idx].isBookmarked()) {
                bookmarks.push(questionParts[idx]);
            }
        }

        return bookmarks;
    }

    /**
     * Gets the preview materials of the sheet.
     * @returns {Array} The preview materials.
     */
    getPreviewMaterials() {
        return this.data['previewMaterials'];
    }

    /**
     * Calculates the total marks of question parts that have been started.
     * @returns {number} The total started marks.
     */
    getTotalStartedMarks() {
        let totalMarks = 0,
            questionParts = this.getQuestionParts(),
            si, isStarted;

        for (si in questionParts) {
            isStarted = questionParts[si].getStatus() > 0 && questionParts[si].getStatus() < 1;
            totalMarks += isStarted ? questionParts[si].getMarks() : 0;
        }

        return totalMarks;
    }

    /**
     * Calculates the total marks of question parts that have not been started.
     * @returns {number} The total not started marks.
     */
    getTotalNotStartedMarks() {
        let totalMarks = 0,
            questionParts = this.getQuestionParts(),
            si, isNotStarted;

        for (si in questionParts) {
            isNotStarted = questionParts[si].getStatus() == 0;
            totalMarks += isNotStarted ? questionParts[si].getMarks() : 0;
        }

        return totalMarks;
    }

    /**
     * Calculates the total marks of question parts that have been finished.
     * @returns {number} The total finished marks.
     */
    getTotalFinishedMarks() {
        let totalMarks = 0,
            questionParts = this.getQuestionParts(),
            si, isFinished;

        for (si in questionParts) {
            isFinished = questionParts[si].getStatus() == 1;
            totalMarks += isFinished ? questionParts[si].getMarks() : 0;
        }
        return totalMarks;
    }

    /**
     * Checks if the sheet contains a specific question part.
     * @param {Object} questionPart - The question part to check.
     * @returns {boolean} True if the sheet contains the question part, false otherwise.
     */
    hasQuestionPart(questionPart) {
        let questionParts = this.getQuestionParts(),
            found, idx;

        for (idx = 0; idx < questionParts.length && !found; idx++) {
            found = (questionParts[idx] == questionPart);
        }

        return found;
    }

    /**
     * Gets the default question part from the sheet.
     * @returns {Object|null} The default question part or null if not found.
     */
    getDefaultQuestionPart() {
        let questionParts = this.getQuestionParts();
        let defaultQuestionPart = null;
        angular.forEach(questionParts, function (value, key) {
            if (defaultQuestionPart === null && value.data.isDefaultQuestionPart) {
                defaultQuestionPart = value;
            }
        });

        return defaultQuestionPart;
    }

    /**
     * Filters the sheet based on the provided filters.
     * @param {Object} filters - The filters to apply.
     */
    filter(filters) {
        let sheetObj = this;
        let rows = this.getLayout().getRows();

        angular.forEach(filters.getRows, function (filter, key) {
            switch (filter.filter) {
                case "byQuestionPart":
                    _filterByQuestionPart(rows, sheetObj.context, filter.value);
                    break;
                case "includedInPDF":
                    _filterByObjectResponsePDF(rows, sheetObj.context, filter.value);
                    break;
            }
        });
    }

    /**
     * Gets a question part by its UUID.
     * @param {string} uuid - The UUID of the question part.
     * @returns {Object|null} The question part or null if not found.
     */
    getQuestionPartByUUID(uuid) {
        let questionParts = this.getQuestionParts(),
            found = null;

        for (let idx = 0; idx < questionParts.length && !found; idx++) {
            console.log("QPART x", idx, questionParts[idx]);
            if (questionParts[idx].getUUID() == uuid) {
                found = questionParts[idx];
            }
        }

        return found;
    }

    /**
     * Filters rows by question part UUID.
     * @param {Array} rows - The rows to filter.
     * @param {Object} context - The context object.
     * @param {string} uuid - The UUID of the question part.
     */
    static _filterByQuestionPart(rows, context, uuid) {
        let rowKey = 0;
        for (rowKey = 0; rowKey < rows.length; rowKey++) {
            let row = rows[rowKey];

            let colKey = 0;
            for (colKey = 0; colKey < row.cols.length; colKey++) {
                let col = row.cols[colKey];

                let listKey;
                for (listKey = 0; listKey < col.list.length; listKey++) {
                    let list = col.list[listKey];
                    if (typeof list !== 'undefined') {
                        let container = context.read(list.content);
                        let questionpart = container.getQuestionPart();
                        if (!questionpart || (questionpart && questionpart.getUUID() !== uuid)) {
                            col.list.splice(listKey, 1);
                            listKey = -1;
                        }
                    }
                }
                if (col.list.length === 0) {
                    row.cols.splice(colKey, 1);
                    colKey = -1;
                }
            }
            if (row.cols.length === 0) {
                rows.splice(rowKey, 1);
                rowKey = -1;
            }
        }
    }

    /**
     * Filters rows by object response PDF inclusion.
     * @param {Array} rows - The rows to filter.
     * @param {Object} context - The context object.
     * @param {boolean} value - The value to filter by.
     */
    static _filterByObjectResponsePDF(rows, context, value) {
        let rowKey = 0;
        for (rowKey = 0; rowKey < rows.length; rowKey++) {
            let row = rows[rowKey];

            let colKey = 0;
            for (colKey = 0; colKey < row.cols.length; colKey++) {
                let col = row.cols[colKey];

                let containers = col.list;
                _filter(containers, context, value);
                if (containers.length === 0) {
                    row.cols.splice(colKey, 1);
                    colKey = -1;
                }
            }
            if (row.cols.length === 0) {
                rows.splice(rowKey, 1);
                rowKey = -1;
            }
        }
    }

    /**
     * Filters containers by object response PDF inclusion.
     * @param {Array} containers - The containers to filter.
     * @param {Object} context - The context object.
     * @param {boolean} value - The value to filter by.
     * @param {boolean} existSubContainer - Indicates if sub-containers exist.
     * @param {string} log - The log message.
     */
    static _filter(containers, context, value, existSubContainer, log) {
        let containerKey;
        for (containerKey = 0; containerKey < containers.length; containerKey++) {
            let containerUUID = containers[containerKey].content;
            if (typeof containerUUID !== 'undefined') {
                let containerObject = context.read(containerUUID);
                let questionpart = containerObject.getQuestionPart();
                if (!questionpart && !existSubContainer) {
                    containers.splice(containerKey, 1);
                    containerKey = -1;
                } else {
                    if (typeof containerObject.getObjects() !== "undefined" && containerObject.getObjects().length > 0) {
                        let objectKey;
                        for (objectKey = 0; objectKey < containerObject.getObjects().length; objectKey++) {
                            let contentObject = context.read(containerObject.getObjects()[objectKey]);
                            if (typeof contentObject.data.preferences === "undefined") {
                                contentObject.data.preferences = {};
                            }
                            if (typeof contentObject.data.preferences.includedInPDF === "undefined") {
                                let originObjProperties = ContentObjectRegistryService.getObjectProperties(contentObject.getObjectType());
                                if (originObjProperties && originObjProperties.obj && originObjProperties.obj.preferences) {
                                    contentObject.data.preferences.includedInPDF = originObjProperties.obj.preferences.includedInPDF;
                                }
                            }
                            if (typeof contentObject.data.preferences.includedInPDF === "undefined" || contentObject.data.preferences.includedInPDF !== value) {
                                containerObject.getObjects().splice(objectKey, 1);
                                objectKey = -1;
                            }
                        }

                        if (containerObject.getObjects().length === 0) {
                            containers.splice(containerKey, 1);
                            containerKey = -1;
                        }
                    } else if (containerObject.isShowCarousels()) {
                        _filter(containerObject.getCarousels(), context, value, true, 'getCarousels');
                    } else if (containerObject.isShowTabs()) {
                        _filter(containerObject.getTabs(), context, value, true, 'getCarousels');
                    } else if (containerObject.isShowIaaContainer()) {
                        _filter(containerObject.getIaaContainer(), context, value, true, 'getCarousels');
                    } else if (containerObject.isShowInteraction()) {
                        // The response lives on the container itself, so an
                        // empty child-object list is expected and must be kept.
                    } else {
                        containers.splice(containerKey, 1);
                        containerKey = -1;
                    }
                }
            }
        }
    }
}

export default Sheet;
